import {
  Component,
  Prop,
  Event,
  EventEmitter,
  Method,
  State,
  Element,
  h
} from '@stencil/core';
import { Color, Size } from '../../interface';
import isArray from 'lodash-es/isArray';
import isObject from 'lodash-es/isObject';
import isNumber from 'lodash-es/isNumber';
import clone from 'lodash-es/clone';
import { DoorState, SlidingDoorState } from './sliding-door-interface';

@Component({
  tag: 'lar-sliding-door',
  styleUrl: 'sliding-door.scss',
  shadow: true
})
export class SlidingDoorNode {
  @Element() el!: HTMLLarAnalogValueElement;

  /////// LarvaNode base properties
  /**
   * Component main icon
   */
  @Prop({ mutable: true }) icon?: string = 'off';
  /**
   * The color to use from your application's color palette.
   * Detrouble options are: `"primary"`, `"secondary"`, `"tertiary"`, `"success"`, `"warning"`, `"danger"`, `"light"`, `"medium"`, and `"dark"`.
   */
  @Prop({ mutable: true }) color?: Color = 'primary';
  /**
   * The color to use from your application's color palette for Components modal window.
   */
  @Prop({ mutable: true }) colorModal?: Color;
  /**
   * The color to use from your application's color palette for inputs
   */
  @Prop({ mutable: true }) colorInputs?: Color;
  /**
   * The color to use from your application's color palette for indication icon
   */
  @Prop() colorIconSmall?: Color;
  /**
   * Disable quck actions
   */
  @Prop() disableQuickActions: boolean = false;
  /**
   * Allow node indication color automatic change based on feedback/node value. Defaults to false
   */
  @Prop() allowIndicationAutoColoring: boolean = false;
  /**
   * Hide node titles
   */
  @Prop() hideTitles?: boolean;
  /**
   * Component superscript title
   */
  @Prop() supTitle?: string;
  /**
   * Component subtitle
   */
  @Prop() subTitle?: string;
  /**
   * Is logging for this component enabled (lar-log subcomponent loaded)
   */
  @Prop() log: boolean = true;
  /**
   * Component main title
   */
  @Prop() mainTitle!: string;
  /**
   * Node size
   */
  @Prop() nodeSize?: Size = 'default';
  /**
   * @see {@link ../readme.md} chapter "Components input and output" for further information.
   */
  @Event() output!: EventEmitter;
  /**
   * @see {@link ../readme.md} chapter "Sub-Components requests and responses" for further information.
   */
  @Event() request!: EventEmitter;
  /////// LarvaNode base properties and events - end

  @State() loading: boolean = true;

  @State() doorState: string;

  @State() fullState: SlidingDoorState = {
    doorState:  undefined,
    doorStateString: DoorState.UNKNOWN,
    slidingTimerRunning: false,
    slidingTimerElapsed: 0,
    slidingTimerTimeout: 0,
    slidingTimerStartedAt: 0,
    stopEnabled: false
  };

  @State() timerValue: number;

  private node!: HTMLLarNodeElement;

  private timer: any;

  componentDidLoad() {
    const el = this.el.shadowRoot || this.el;
    this.node = el.querySelector('lar-node') as HTMLLarNodeElement;
    this.output.emit({ command: 'get' });
  }

  /**
   * Larva error input
   */
  @Method()
  async error(data: any) {
    if (this.node) {
      this.node.error(data);
    }
    this.loading = false;
  }
  /**
   * Input Larva event message (see above)
   */
  @Method()
  async input(data: SlidingDoorState) {
    if (isArray(data)) {
      // this is the door output message with [open, close, stop] booleans
    } else if (isObject(data)) {
      const lastState = clone(this.fullState);
      this.fullState = data;

      if (this.fullState.slidingTimerRunning && isNumber(this.fullState.slidingTimerElapsed) && isNumber(this.fullState.slidingTimerTimeout)) {
        if (!this.timer || lastState.slidingTimerStartedAt !== this.fullState.slidingTimerStartedAt) {
          // restart the timer if slidingTimerStartedAt has changed
          this.startTimer();
        }
      }
      if (!this.fullState.slidingTimerRunning && this.timer) {
        this.resetTimer();
      }
    }
    this.loading = false;
  }

  private resetTimer() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  private startTimer() {
    this.timerValue = this.fullState.slidingTimerElapsed;
    const intervalTime = 1000;
    this.resetTimer();
    this.timer = setInterval(() => {
      try {
        if (!this.fullState.slidingTimerRunning || this.timerValue > this.fullState.slidingTimerTimeout) {
          this.resetTimer();
          // timer should have been stopped by now
          // make read request to get the state
          this.output.emit({ command: 'get' });
          this.loading = true;
        }
        this.timerValue += intervalTime;
      } catch(err) {
        console.error(err);
      }
    }, intervalTime);
  }

  private sendCommand(cmd: string) {
    this.output.emit({ command: cmd });
    this.loading = true;
  }

  render() {
    let value = this.loading ? '' : 'slidingDoor.stateUnknown';
    let iconSmall = '';
    let colorIconSmall = this.colorIconSmall;
    switch (this.fullState.doorStateString) {
      case DoorState.CLOSED: {
        value = 'slidingDoor.stateClosed';
        iconSmall = 'lock';
        colorIconSmall = this.allowIndicationAutoColoring ? 'success' : this.colorIconSmall;
        break;
      }
      case DoorState.OPEN: {
        value = 'slidingDoor.stateOpen';
        iconSmall = 'unlock';
        colorIconSmall = this.allowIndicationAutoColoring ? 'danger' : this.colorIconSmall;
        break;
      }
      case DoorState.OPENING: {
        iconSmall = 'moving-cogs';
        value = 'slidingDoor.stateOpening';
        break;
      }
      case DoorState.CLOSING: {
        iconSmall = 'moving-cogs';
        value = 'slidingDoor.stateClosing';
        break;
      }
    }
    const componentProps = {
      doorState: this.fullState.doorStateString,
      stopEnabled: Boolean(this.fullState.stopEnabled),
      color: this.colorInputs || this.color,
      loading: this.loading,
      onSendcommand: (ev: CustomEvent) => this.sendCommand(ev.detail),
    };
    return (
      <lar-node
        value={value}
        hideTitles={this.hideTitles}
        icon={this.icon}
        iconSmall={iconSmall}
        color={this.color}
        supTitle={this.supTitle}
        mainTitle={this.mainTitle}
        subTitle={this.subTitle}
        colorModal={this.colorModal}
        colorInputs={this.colorInputs}
        colorIconSmall={colorIconSmall}
        loading={this.loading}
        log={this.log}
        nodeSize={this.nodeSize}
        component="sliding-door-content"
        componentProps={componentProps}
      >
        {this.disableQuickActions === false &&
          <lar-button-push
            size="small"
            slot="titles"
            color={this.colorInputs || this.color}
            disabled={this.loading || this.fullState.slidingTimerRunning || !this.fullState.doorStateString || this.fullState.doorStateString === DoorState.UNKNOWN || this.fullState.doorStateString === DoorState.OPENING || this.fullState.doorStateString === DoorState.CLOSING}
            onClick={ev => this.fullState.doorStateString === DoorState.CLOSED ? this.sendCommand('open') : this.sendCommand('close')}
          />
        }
        <slot />
      </lar-node>
    );
  }
}
